import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Clock, CheckCircle, Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fetchCourseById } from '@/services/courses';
import { fetchLessonsByCourseId } from '@/services/lessons';
import { completeLesson, getCourseProgress, resetCourseProgress, getLessonProgress } from '@/services/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { validateCourseAccess } from '@/services/courses';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

interface Lesson {
  id: string;
  title: string;
  orderIndex: number;
  completed: boolean;
  isCompleted?: boolean;
  completedAt?: string | null;
}

interface CourseDetails {
  id: string;
  title: string;
  description: string;
  coverImage?: string;
  totalLessons: number;
  estimatedDuration: number;
  estimatedTime?: string;
  progress: number;
  completedLessons: number;
  lessons: Lesson[];
}

const Course: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(true);
  
  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Add explicit debug code to ensure path is saved
  useEffect(() => {
    if (!isAuthenticated && id) {
      const path = `/course/${id}`;
      console.log(`Debug - Explicitly saving path: ${path}`);
      sessionStorage.setItem('auth_redirect_path', path);
    }
  }, [isAuthenticated, id]);
  
  // Fetch course data with proper error handling
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!id) {
        setError("Course ID is missing");
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        // First validate if user has access to this course
        const canAccess = await validateCourseAccess('User');
        
        if (!canAccess) {
          setHasAccess(false);
          setIsLoading(false);
          return;
        }
        
        // Fetch course details
        const courseData = await fetchCourseById(id);
        
        if (!courseData) {
          setError("Course not found");
          setIsLoading(false);
          return;
        }
        
        // Fetch lessons for this course
        const lessonsData = await fetchLessonsByCourseId(id);
        
        // Fetch course progress
        const progressData = await getCourseProgress(id);
        
        // Create a map of completed lessons
        const completedLessonIds = new Set<string>();
        let completedCount = 0;
        let progressPercentage = 0;
        
        if (progressData) {
          completedCount = progressData.completedLessons;
          progressPercentage = progressData.progress;
          
          // Get progress entries for the course
          const progressEntries = await getCourseProgress(id);
          
          // Update the lesson completion check to use isCompleted from the lessons data or completedLessonIds
          // When we receive the lessons from the fetchLessonsByCourseId, check if they have isCompleted
          // set to true and add them to the completed lessons set
          lessonsData.forEach(lesson => {
            if (lesson.isCompleted) {
              completedLessonIds.add(lesson.id);
            }
          });
          
          // If we don't have any completed lessons identified yet, fallback to the older method
          if (completedLessonIds.size === 0 && progressEntries && progressEntries.completedLessons > 0) {
            // Get progress for individual lessons
            await Promise.all(
              lessonsData.map(async (lesson) => {
                const progress = await getLessonProgress(lesson.id);
                if (progress && progress.completed) {
                  completedLessonIds.add(lesson.id);
                }
                return lesson;
              })
            );
          }
          
          // Update completedCount based on the Set
          completedCount = completedLessonIds.size;
          
          // Additional safety check: ensure progress is 100% only if all lessons are completed
          if (lessonsData.length > 0 && completedCount === lessonsData.length) {
            progressPercentage = 100;
          } else if (completedCount < lessonsData.length) {
            // If not all lessons are completed, make sure progress is less than 100%
            progressPercentage = Math.min(progressPercentage, 99);
          }
        }
        
        // Mark lessons as completed based on progress data and the API's isCompleted flag
        const formattedLessons = lessonsData.map(lesson => {
          // Check both isCompleted from API and completedLessonIds from our progress check
          const isCompleted = lesson.isCompleted || completedLessonIds.has(lesson.id);
          return {
            id: lesson.id,
            title: lesson.title,
            orderIndex: lesson.orderIndex || 0,
            completed: isCompleted,
            isCompleted: lesson.isCompleted,
            completedAt: lesson.completedAt
          };
        });
        
        // Sort lessons by orderIndex
        formattedLessons.sort((a, b) => a.orderIndex - b.orderIndex);
        
        // Get the image URL - API provides it as imageUrl (lowercase)
        const courseImageUrl = courseData.imageUrl || courseData.coverImage;
        
        // Create the full course object
        const courseDetails: CourseDetails = {
          id: courseData.id,
          title: courseData.title,
          description: courseData.description || '',
          coverImage: courseImageUrl || "/thumbnail.webp",
          totalLessons: lessonsData.length,
          estimatedDuration: courseData.estimatedDuration || 0,
          estimatedTime: courseData.estimatedTime || '',
          progress: lessonsData.length > 0 
            ? Math.floor((completedCount / lessonsData.length) * 100) 
            : 0,
          completedLessons: completedCount,
          lessons: formattedLessons
        };
        
        setCourse(courseDetails);
      } catch (error) {
        console.error('Error loading course:', error);
        setError("Failed to load course data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && user && id) {
      fetchCourseData();
    } else if (!isAuthenticated) {
       // Hook handles redirect, set loading false
       setIsLoading(false);
    }
  }, [isAuthenticated, user, id, navigate]);
  
  // Format duration in minutes to readable string
  const formatDuration = (minutes: number): string => {
    if (minutes === 0) {
      return 'No duration set';
    } else if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
      }
    }
  };

  // Start the course - navigate to first lesson
  const startCourse = () => {
    if (course?.lessons && course.lessons.length > 0) {
      navigate(`/lesson/${course.lessons[0].id}`);
    }
  };
  
  // Continue the course - navigate to first incomplete lesson
  const continueCourse = () => {
    if (!course?.lessons) return;
    
    const firstIncompleteLesson = course.lessons.find(lesson => !lesson.completed);
    if (firstIncompleteLesson) {
      navigate(`/lesson/${firstIncompleteLesson.id}`);
    } else if (course.lessons.length > 0) {
      // If all lessons are complete, go to the last one
      navigate(`/lesson/${course.lessons[course.lessons.length - 1].id}`);
    }
  };
  
  // Calculate if all lessons are completed
  const allLessonsCompleted = useMemo(() => {
    if (!course) return false;
    
    // If no lessons, return false
    if (!course.lessons || course.lessons.length === 0) return false;
    
    // Check if all lessons are completed (checking both flags)
    return course.lessons.every(lesson => lesson.completed || lesson.isCompleted);
  }, [course]);

  // Reset progress
  const resetProgress = async () => {
    if (!id) return;
    
    try {
      toast.info('Resetting course progress...');
      
      // Call the API to reset progress
      await resetCourseProgress(id);
      
      // Update local state
      if (course) {
        setCourse({
          ...course,
          progress: 0,
          completedLessons: 0,
          lessons: course.lessons.map(lesson => ({ ...lesson, completed: false }))
        });
      }
      
      toast.success('Course progress has been reset');
    } catch (error) {
      console.error("Failed to reset progress:", error);
      toast.error('Failed to reset progress');
    }
  };

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (!course) return 0;
    
    // If no lessons, return 0
    if (!course.lessons || course.lessons.length === 0) return 0;
    
    // Count completed lessons - check both completed and isCompleted flags
    const completedLessonsCount = course.lessons.filter(
      lesson => lesson.completed || lesson.isCompleted
    ).length;
    
    // Only set to 100% if all lessons are actually completed
    if (completedLessonsCount === course.lessons.length && completedLessonsCount > 0) {
      return 100;
    }
    
    // Otherwise calculate percentage, but cap at 99% until all lessons are done
    const calculated = Math.floor((completedLessonsCount / course.lessons.length) * 100);
    return Math.min(calculated, 99);
  }, [course]);

  // Find the index of the first incomplete lesson
  const firstIncompleteLessonIndex = useMemo(() => {
    if (!course?.lessons) return -1; // Handle case where lessons aren't loaded
    // Find the index based on the sorted lessons array
    return course.lessons.findIndex(lesson => !(lesson.completed || lesson.isCompleted));
  }, [course]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <div className="flex flex-col items-center justify-center h-80">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading course...</p>
          </div>
        </main>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button variant="ghost" asChild>
              <Link to="/courses">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Courses
              </Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }
  
  // Access denied state
  if (!hasAccess) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to view this course. Please log in with appropriate credentials.
            </AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button variant="ghost" asChild>
              <Link to="/courses">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Courses
              </Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }
  
  // No course found or not loaded yet
  if (!course) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <Alert>
            <AlertTitle>Course Not Found</AlertTitle>
            <AlertDescription>The course you're looking for could not be found.</AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button variant="ghost" asChild>
              <Link to="/courses">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Courses
              </Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/courses" className="inline-flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Courses
          </Link>
        </Button>
        
        {/* Course Header */}
        <div className="relative overflow-hidden rounded-xl bg-card border">
          <div className="h-64 w-full overflow-hidden">
            <img 
              src={course.coverImage || "/thumbnail.webp"} 
              alt={course.title} 
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent"></div>
          </div>
          
          <div className="relative p-6 sm:p-8">
            <div className="mb-4 flex flex-wrap gap-3">
              <Badge variant="outline" className="px-2 py-1">
                <BookOpen className="mr-1 h-4 w-4" /> {course.totalLessons} Lessons
              </Badge>
              <Badge variant="outline" className="px-2 py-1">
                <Clock className="mr-1 h-4 w-4" /> {course.estimatedTime || formatDuration(course.estimatedDuration)}
              </Badge>
            </div>
            
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {course.title}
            </h1>
            
            <p className="mt-4 max-w-prose text-muted-foreground">
              {course.description}
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {course.progress === 0 ? (
                <Button onClick={startCourse} size="lg" className="flex-1 sm:flex-initial">
                  Start Course
                </Button>
              ) : course.progress < 100 || !allLessonsCompleted ? (
                <>
                  <Button onClick={continueCourse} size="lg" className="flex-1 sm:flex-initial">
                    Continue Course
                  </Button>
                  <Button onClick={resetProgress} variant="outline" size="lg" className="flex-1 sm:flex-initial">
                    Restart Course
                  </Button>
                </>
              ) : (
                // Only show these buttons when all lessons are completed (progress is 100% AND all lessons are completed)
                <>
                  <Button onClick={() => navigate(`/certificate/${course.id}`)} size="lg" className="flex-1 sm:flex-initial">
                    View Certificate
                  </Button>
                  <Button onClick={resetProgress} variant="outline" size="lg" className="flex-1 sm:flex-initial">
                    Restart Course
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Course Progress */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Your Progress</CardTitle>
            <CardDescription>
              {course.completedLessons} of {course.totalLessons} lessons completed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{course.progress}% complete</span>
                <span>{course.completedLessons}/{course.totalLessons} lessons</span>
              </div>
              <Progress value={course.progress} className="h-2" />
            </div>
          </CardContent>
        </Card>
        
        {/* Course Lessons */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Course Content</CardTitle>
            <CardDescription>
              Complete all lessons to finish the course
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {course.lessons.map((lesson, index) => {
                const isCompleted = lesson.completed || lesson.isCompleted;
                const isNextLesson = firstIncompleteLessonIndex !== -1 && index === firstIncompleteLessonIndex;
                const isEnabled = isCompleted || isNextLesson;

                return (
                  <li key={lesson.id}>
                    {isEnabled ? (
                      <Link
                        to={`/lesson/${lesson.id}`}
                        className="flex items-center justify-between rounded-md border p-4 transition-colors hover:bg-accent hover:text-accent-foreground"
                        aria-disabled={!isEnabled}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                              isCompleted 
                                ? 'bg-green-500/20 text-green-600' 
                                : 'bg-primary/10 text-primary'
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              lesson.orderIndex + 1
                            )}
                          </div>
                          <span className={`font-medium ${isCompleted ? 'text-muted-foreground' : ''}`}>
                            {lesson.title}
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                    ) : (
                      <div
                        className="flex items-center justify-between rounded-md border p-4 text-muted-foreground opacity-60 cursor-not-allowed"
                        title="Complete previous lessons to unlock this one"
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className={`flex h-8 w-8 items-center justify-center rounded-full bg-muted/50 text-muted-foreground`}
                          >
                            {lesson.orderIndex + 1}
                          </div>
                          <span className={`font-medium`}>
                            {lesson.title}
                          </span>
                        </div>
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
          <CardFooter className="flex justify-center border-t bg-muted/20 px-6 py-4">
            <p className="text-sm text-muted-foreground">
              Complete all lessons to earn your certificate
            </p>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
};

export default Course;
